import React, { useState, useEffect } from "react";
import { Modal, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OTPInput from "react-otp-input";
import { toast } from "react-toastify";
import { useSendOtpMutation, useVerifyOtpMutation } from "../service/auth/api";

interface OtpVerifyModalProps {
    open: boolean;
    onClose: () => void;
    targetIdentifier: string; // The email or phone number string
    targetType: "email" | "phone";
    countryCode?: string; // Required if targetType is 'phone'
    onVerifySuccess: () => void;
}

const OtpVerifyModal: React.FC<OtpVerifyModalProps> = ({
    open,
    onClose,
    targetIdentifier,
    targetType,
    countryCode,
    onVerifySuccess,
}) => {
    const [otp, setOtp] = useState<string>("");
    const [timer, setTimer] = useState<number>(30);
    const [canResend, setCanResend] = useState<boolean>(false);

    const [sendOtp, { isLoading: isResending }] = useSendOtpMutation();
    const [verifyOtp, { isLoading: isVerifying }] = useVerifyOtpMutation();

    // Reset state when modal opens
    useEffect(() => {
        if (open) {
            setOtp("");
            setTimer(30);
            setCanResend(false);
            // Trigger initial OTP send? 
            // User flow implies clicking "Verify" opens modal. 
            // Usually that click should trigger the send OR the modal opening triggers it.
            // Let's trigger it here if it's a fresh open.
            handleSendOtp();
        }
    }, [open]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else {
            setCanResend(true);
        }
        return () => clearInterval(interval);
    }, [timer]);

    const handleSendOtp = async () => {
        try {
            const payload: any = {
                otpType: 4, // Verification
            };
            if (targetType === "email") {
                payload.email = targetIdentifier;
            } else {
                payload.phone = targetIdentifier;
                payload.countryCode = countryCode;
            }

            const result = await sendOtp(payload).unwrap();
            toast.success(result.message || "OTP sent successfully");
            setTimer(30);
            setCanResend(false);
        } catch (err: any) {
            console.error("Send OTP Error:", err);
            toast.error(err?.data?.message || "Failed to send OTP");
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (otp.length !== 4) {
            toast.error("Please enter a valid 4-digit OTP");
            return;
        }

        try {
            const payload: any = {
                otp: otp,
                otpType: 4,
                role: 1, // Admin/Vendor
                deviceType: "WEB",
                deviceToken: "dummy_token"
            };

            if (targetType === "email") {
                payload.email = targetIdentifier;
            } else {
                payload.phone = targetIdentifier;
                payload.countryCode = countryCode;
            }

            const result = await verifyOtp(payload).unwrap();
            toast.success(result.message || "Verified successfully");
            onVerifySuccess();
            onClose();
        } catch (err: any) {
            console.error("Verify OTP Error:", err);
            toast.error(err?.data?.message || "Invalid OTP");
            setOtp("");
        }
    };

    return (
        <Modal
            className="modal addNotification_modal"
            open={open}
            onClose={onClose}
        >
            <div className="modal-dialog">
                <div className="modal-body">
                    <div className="modal_title">
                        <h2>Verify {targetType === "email" ? "Email" : "Phone"}</h2>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    <div className="mb_20 hd_6">
                        <h3>
                            Please enter the OTP sent to {targetIdentifier}
                        </h3>
                    </div>

                    <form className="form" onSubmit={handleSubmit}>
                        <div className="control_group">
                            <div className="opt_fields" style={{ justifyContent: "center" }}>
                                <OTPInput
                                    value={otp}
                                    onChange={setOtp}
                                    numInputs={4}
                                    renderInput={(props) => <input {...props} />}
                                    inputType="tel"
                                    shouldAutoFocus
                                />
                            </div>
                            <div className="auth_bottom" style={{ textAlign: "center", marginTop: "1rem" }}>
                                <button
                                    type="button"
                                    className="link"
                                    onClick={handleSendOtp}
                                    disabled={!canResend || isResending}
                                    style={{ opacity: canResend ? 1 : 0.5, cursor: canResend ? 'pointer' : 'not-allowed', border: 'none', background: 'none', color: 'blue' }}
                                >
                                    {isResending ? "Resending..." : canResend ? "Resend OTP" : `Resend OTP (${timer}s)`}
                                </button>
                            </div>
                        </div>

                        <div className="footer_btn_flex">
                            <Button
                                className="btn btn_grey"
                                onClick={onClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="btn btn_primary"
                                disabled={isVerifying}
                            >
                                {isVerifying ? "Verifying..." : "Verify"}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </Modal>
    );
};

export default OtpVerifyModal;
